import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { debug } from '@/utils/debug';
import { hasStoredAuthTokens } from '@/utils/session';
import { LoadingSpinner } from '@/components/ui';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const location = useLocation();

  // Track if we've completed the initial auth check
  // Start as true if tokens exist (we need to verify them first)
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const hasTokens = hasStoredAuthTokens();

  useEffect(() => {
    debug.log('ProtectedRoute: Effect triggered', { isAuthenticated, isLoading, hasTokens, hasCheckedAuth });
    // Check for existing authentication on mount
    if (!isAuthenticated && !isLoading && !hasCheckedAuth) {
      debug.log('ProtectedRoute: Calling checkAuth()');
      checkAuth().finally(() => {
        setHasCheckedAuth(true);
      });
    } else if (isAuthenticated) {
      // Already authenticated, mark as checked
      setHasCheckedAuth(true);
    }
  }, [isAuthenticated, isLoading, checkAuth, hasCheckedAuth, hasTokens]);

  // Show loading state while checking auth OR if tokens exist but haven't been verified yet
  if (isLoading || (hasTokens && !hasCheckedAuth && !isAuthenticated)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" label="Checking authentication" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated (and we've finished checking)
  if (!isAuthenticated) {
    debug.log('ProtectedRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  debug.log('ProtectedRoute: User authenticated, rendering children');
  return <Outlet />;
}